import { CalendarDaysIcon, CalendarIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import PageEmptyState from 'pollworker/src/components/PageEmptyState';
import WorkScheduleCalendarView from 'pollworker/src/components/WorkScheduleCalendarView';
import WorkScheduleTableView from 'pollworker/src/components/WorkScheduleTableView';
import { ConfirmationStatus } from 'pollworker/src/enums/confirmationStatus';
import { getWorkSchedule } from 'pollworker/src/fetchers';
import { useAppDispatch, useAppSelector } from 'pollworker/src/hooks';
import { selectCurrentUserWorkSchedules, setCurrentUserWorkSchedules } from 'pollworker/src/state/schedule';
import type { WorkSchedule } from 'pollworker/src/types/WorkSchedule';
import type React from 'react';
import { type ReactNode, useEffect, useState } from 'react';
import { Flexor, Spinner } from 'shared/src/components';
import { Tabs, TabsList, TabsTrigger } from 'shared/src/components/ui/new';
import { cn } from 'shared/src/utils';
import { useLocation, useParams } from 'wouter';

export function renderDataAttribute(data: number | string | null | undefined) {
  const isMissing = data === null || data === undefined || data === '';
  return <span className={cn(isMissing ? 'italic text-gray-600' : '')}>{isMissing ? 'Not specified' : data}</span>;
}

export default function WorkSchedules() {
  const dispatch = useAppDispatch();
  const selectedElectionId = useAppSelector((state) => state.election.electionId);
  const workSchedules = useAppSelector(selectCurrentUserWorkSchedules) ?? [];
  const [loadingWorkSchedules, setLoadingWorkSchedules] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const { viewName = 'table' } = useParams();
  const [, navigate] = useLocation();
  const view = viewName === 'calendar' ? 'calendar' : 'table';

  useEffect(() => {
    if (!selectedElectionId) return;

    loadWorkSchedules();
  }, [selectedElectionId]);

  function loadWorkSchedules() {
    setLoadingWorkSchedules(true);

    getWorkSchedule(selectedElectionId)
      .then((response: WorkSchedule[]) => {
        dispatch(setCurrentUserWorkSchedules(response));
      })
      .catch(() => {
        setErrorMessage('Could not load work schedules');
      })
      .finally(() => setLoadingWorkSchedules(false));
  }

  return (
    <div className="mt-2">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900 mb-4">Work Schedule</h1>
          <div className="lg:flex justify-between items-center">
            <div className="space-y-2 text-sm text-gray-700">
              <p>Your work schedule is displayed here. You are currently viewing the schedule in grid format.</p>
              <p>Use the "Change" or "Respond" buttons below to alert staff which scheduled times you are able to fulfill.</p>
            </div>

            <Flexor justify="center" className="mt-4 lg:mt-0">
              <Tabs value={view} onValueChange={(value) => navigate(value === 'calendar' ? '/work_schedule/calendar' : '/work_schedule')}>
                <TabsList>
                  <TabsTrigger value="table" className="gap-2">
                    <TableCellsIcon className="size-4" />
                    Table
                  </TabsTrigger>
                  <TabsTrigger value="calendar" className="gap-2">
                    <CalendarDaysIcon className="size-4" />
                    Calendar
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </Flexor>
          </div>
        </div>
      </div>

      <div className="-mx-4 mt-8 sm:-mx-0 overflow-x-auto">
        {loadingWorkSchedules && !workSchedules.length ? (
          <div className="w-full flex justify-center items-center h-24 my-24 space-x-2">
            <Spinner show />
            <span className="text-lg">Loading work schedules...</span>
          </div>
        ) : null}
        {!loadingWorkSchedules && !workSchedules.length ? (
          <PageEmptyState
            icon={CalendarIcon}
            errorMessage={errorMessage}
            emptyMessageHeader="No work schedules"
            emptyMessageSubHeader="Check back soon to keep up-to-date with your schedule."
            reload={loadWorkSchedules}
          />
        ) : null}
        {workSchedules.length ? (
          <div className={cn(loadingWorkSchedules ? 'opacity-25' : '', 'transition-opacity')}>
            {viewName === 'table' ? <WorkScheduleTableView workSchedules={workSchedules} /> : null}
            {viewName === 'calendar' ? <WorkScheduleCalendarView workSchedules={workSchedules} /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function getStatusLabel(workSchedule: WorkSchedule): ReactNode {
  switch (workSchedule.ConfirmationStatus) {
    case ConfirmationStatus.Confirmed:
      return (
        <>
          <div className="mr-0.5 rounded-full bg-green-500 h-3 w-3" />
          <span>Accepted</span>
        </>
      );
    case ConfirmationStatus.Declined:
      return (
        <>
          <div className="mr-0.5 rounded-full bg-red-500 h-3 w-3" />
          <span>Declined</span>
        </>
      );
    case ConfirmationStatus['No Response']:
      return (
        <>
          <div className="mr-0.5 rounded-full bg-gray-500 h-3 w-3" />
          <span>No response</span>
        </>
      );
    default:
      return null;
  }
}
